import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { AdminFeedbackRequest } from '@fois-utsavvoice/shared';
import { disableFeedbackRequest, getFeedbackRequest } from '../api/admin.js';
import { useAuth } from '../auth/AuthContext.js';
import { AdminNav } from '../components/AdminNav.js';
import { PageShell } from '../components/PageShell.js';

export function AdminRequestDetailPage() {
  const { requestId } = useParams<{ requestId: string }>();
  const auth = useAuth();
  const [request, setRequest] = useState<AdminFeedbackRequest | undefined>();
  const [error, setError] = useState<string | undefined>();
  const [disabling, setDisabling] = useState(false);

  useEffect(() => {
    if (!auth.tokens || !requestId) {
      return;
    }
    getFeedbackRequest(auth.tokens.idToken, requestId)
      .then(setRequest)
      .catch(() => setError('The feedback request could not be loaded.'));
  }, [auth.tokens, requestId]);

  async function onDisable(): Promise<void> {
    if (!auth.tokens || !requestId) {
      return;
    }
    setDisabling(true);
    try {
      setRequest(await disableFeedbackRequest(auth.tokens.idToken, requestId));
    } catch {
      setError('This request can no longer be disabled.');
    } finally {
      setDisabling(false);
    }
  }

  return (
    <PageShell>
      <AdminNav />
      <section className="card mx-auto max-w-2xl">
        <p>
          <Link className="font-semibold text-crimson underline" to="/admin/requests">
            Back to requests
          </Link>
        </p>
        <h1 className="mt-4 heading-xl text-crimson">Feedback request</h1>
        {error ? (
          <p className="mt-3 text-sm font-semibold text-crimson" role="alert">
            {error}
          </p>
        ) : null}
        {request ? (
          <dl className="mt-6 grid gap-3 sm:grid-cols-2">
            <div>
              <dt className="text-sm font-semibold text-charcoal/70">Order</dt>
              <dd>{request.orderReference}</dd>
            </div>
            <div>
              <dt className="text-sm font-semibold text-charcoal/70">Product</dt>
              <dd>{request.productName}</dd>
            </div>
            <div>
              <dt className="text-sm font-semibold text-charcoal/70">Status</dt>
              <dd>{request.status}</dd>
            </div>
            <div>
              <dt className="text-sm font-semibold text-charcoal/70">Expires</dt>
              <dd>{new Date(request.expiresAt * 1000).toLocaleDateString()}</dd>
            </div>
          </dl>
        ) : (
          <p className="mt-4" role="status">
            Loading request…
          </p>
        )}
        {request?.status === 'ACTIVE' ? (
          <button
            className="btn-secondary mt-6"
            type="button"
            disabled={disabling}
            onClick={onDisable}
          >
            {disabling ? 'Disabling…' : 'Disable link'}
          </button>
        ) : null}
      </section>
    </PageShell>
  );
}
